import { graphql, PageProps } from 'gatsby';
import React from 'react';
import { ArticleSectionContentfulModel } from 'src/api/models/ArticleGroup';
import { ButtonContentfulModel } from 'src/api/models/Button';
import { HeadingContentfulModel } from 'src/api/models/Heading';
import { ResourcesBannerSectionContentfulModel, ResourcesCTASectionContentfulModel } from 'src/api/models/Resources';
import { ShortParagraphContentModel } from 'src/api/models/ShortParagraph';
import Layout from 'src/elements/layout/layout';
import CallForActionWithDescription from 'src/molecules/callToAction/callToAction.withDescription';
import ArticleGroupResourceCenter from 'src/organisms/articles/resourceCenter/article.group';
import ResourceCenterMenu from 'src/organisms/articles/resourceCenter/resourceCenter.menu';
import BannerLanding from 'src/organisms/banners/banner.resourceCenter';
import PromotionModal from 'src/organisms/promotion/PromotionModal';

interface ResourceCenterPageProps extends PageProps {
  data: {
    contentfulPage: {
      title: string;
      seoTitle: string;
      seoDescription?: string;
      displayPromotionModal?: boolean;
      seoImage?: {
        file?: {
          url: string;
        }
      }
      contentfulchildren: (ArticleSectionContentfulModel | ResourcesBannerSectionContentfulModel | ResourcesCTASectionContentfulModel)[];
    }
  }
}


const ResourceCenterPage = (props: ResourceCenterPageProps): JSX.Element => {
  const helmetData = {
    title: props.data.contentfulPage.seoTitle,
    description: props.data.contentfulPage.seoDescription,
    previewImageURL: props.data.contentfulPage.seoImage?.file?.url
  }
  const displayModal = props.data.contentfulPage?.displayPromotionModal;

  // get Banner Landing
  const bannerLandingSection = (props.data.contentfulPage.contentfulchildren as ResourcesBannerSectionContentfulModel[])
    .find((entry) => entry.label.includes('Resource Center Banner Container')).contentfulchildren;
  const bannerLandingTitle = bannerLandingSection.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel;
  const bannerLandingSubtitle = bannerLandingSection.find(data => data.internal.type === 'ContentfulShortParagraph') as ShortParagraphContentModel;

  // get Article Groups
  const contentfulData = (props.data.contentfulPage.contentfulchildren as ArticleSectionContentfulModel[])
    .find((entry) => entry.label.includes('Resource Center Articles')).contentfulchildren;
  const articleGroups = contentfulData.filter((data) => data.internal.type === 'ContentfulArticleGroup');

  // get CTA container
  const callForActionSection = (props.data.contentfulPage.contentfulchildren as ResourcesCTASectionContentfulModel[])
    .find((entry) => entry.label.includes('Resource Center CTA Container')).contentfulchildren;
  const callForActionTitle = callForActionSection.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel;
  const callForActionDescription = callForActionSection.find(data => data.internal.type === 'ContentfulShortParagraph') as ShortParagraphContentModel;
  const callForActionButton = callForActionSection.find(data => data.internal.type === 'ContentfulButton') as ButtonContentfulModel;

  return (
    <Layout helmet={helmetData} currentPageId="6xpWy8hdGSDARWax2mToYE" location={props.location}>

      <BannerLanding
        title={bannerLandingTitle.text}
        description={bannerLandingSubtitle.text}
      />
      <ResourceCenterMenu />
      {
        articleGroups.map((group, index) => (
          <ArticleGroupResourceCenter articleGroup={group} key={group.id} index={index} />
        ))
      }
      <CallForActionWithDescription
        title={callForActionTitle.text}
        description={callForActionDescription.text}
        buttonSize="1em"
        actionButton={callForActionButton}
      />
      <PromotionModal displayModal={displayModal}/>
    </Layout>
  )
}

export const pageQuery = graphql`
  query ResourceCenterPageQuery {
    contentfulPage(contentful_id: {eq: "6xpWy8hdGSDARWax2mToYE"}) {
      title
      seoTitle
      seoDescription
      seoImage {
        file {
          url
        }
      }
      displayPromotionModal
      contentfulchildren {
        id
        label
        contentfulchildren {
          ...ArticleContent
          ...HeadingContent
          ...ShortParagraphContent
          ...ButtonContent
        }
      }
    }
  }
`

export default ResourceCenterPage;
